import React, { useState, useCallback, useEffect, useRef } from "react";
import { graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import classNames from "classnames";
import { Link } from "./link";
import UtilityLink from "./utility-link";

const linKFromSlug = (slug) =>
  `${slug.startsWith("/") ? "" : "/"}${slug}${slug.endsWith("/") ? "" : "/"}`;

const Navigation = ({ data, mobileMenuVisible, bannerTopMenuHeight, headerHeight, bannerTop }) => {
  const {
    mainMenuFiltered: { mainMenuFieldSet },
    labelManagerLabels,
  } = data;

  const [openIdx, setOpenIdx] = useState();
  const toggleItem = useCallback((e) => {
    const clickIdx = parseInt(e.target.name);
    setOpenIdx((value) => (clickIdx === value ? -1 : clickIdx));
  }, []);
  const closeItem = useCallback(() => setOpenIdx(-1), []);
  useEffect(() => {
    // Close submenu when header scrolls up or when mobile menu is closed
    if (!mobileMenuVisible) {
      closeItem();
    }
  }, [closeItem, mobileMenuVisible]);

  const menuRef = useRef();
  useEffect(() => {
    if (openIdx >= 0) {
      const listener = (event) => {
        if (
          event.target !== menuRef.current &&
          menuRef.current &&
          !menuRef.current.contains(event.target)
        ) {
          setOpenIdx(-1);
        }
      };
      document.addEventListener("click", listener);
      return () => document.removeEventListener("click", listener);
    }
  }, [openIdx]);

  return (
    <nav className={`navigation${openIdx >= 0 ? " open" : ""}`}>
      <ul
        ref={menuRef}
        className="navigation__list d-flex flex-column flex-lg-row justify-content-lg-center"
      >
        {mainMenuFieldSet.map((item, i) => (
          <li
            className={`navigation__item${openIdx === i ? " active" : ""}`}
            key={item.mainMenu.id}
          >
            {item.categoryMenuFieldSet && item.categoryMenuFieldSet ? (
              <button
                className="navigation__btn navigation__btn--content"
                onClick={toggleItem}
                name={i}
              >
                {item.mainMenu.liferayFields.title}
              </button>
            ) : item.mainMenu.slug ? (
              <Link className="navigation__btn" to={linKFromSlug(item.slug)}>
                {item.mainMenu.liferayFields.title}
              </Link>
            ) : item.mainMenu.externalLink ? (
              <a
                className="navigation__btn"
                href={item.mainMenu.externalLink}
                target={item.mainMenu.target ? item.mainMenu.target?.[0] : ""}
                rel={
                  item.mainMenu.target && item.mainMenu.target?.[0] === "_blank"
                    ? "noopener noreferrer"
                    : ""
                }
              >
                {item.mainMenu.liferayFields.title}
              </a>
            ) : (
              <span className="navigation__btn">{item.mainMenu.liferayFields.title}</span>
            )}
            <div className="navigation__megamenu">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-12">
                    <button
                      className="navigation__close d-none d-lg-block"
                      onClick={closeItem}
                      aria-label="Close"
                    ></button>
                    <div className="d-none d-lg-block text-center">
                      {item.mainMenu.slug ? (
                        <Link className="navigation__link" to={linKFromSlug(item.mainMenu.slug)}>
                          {item.mainMenu.liferayFields.title}
                        </Link>
                      ) : item.mainMenu.externalLink ? (
                        <a
                          className="navigation__link"
                          href={item.mainMenu.externalLink}
                          target={item.mainMenu.target ? item.mainMenu.target?.[0] : ""}
                          rel={
                            item.mainMenu.target && item.mainMenu.target?.[0] === "_blank"
                              ? "noopener noreferrer"
                              : ""
                          }
                        >
                          {item.mainMenu.liferayFields.title}
                        </a>
                      ) : (
                        <span className="navigation__link">
                          {item.mainMenu.liferayFields.title}
                        </span>
                      )}
                    </div>
                    <div
                      className={classNames(
                        "row",
                        item.categoryMenuFieldSet?.length > 7 && "row-cols-5",
                        {
                          "justify-content-lg-center": item.categoryMenuFieldSet?.length < 5,
                        }
                      )}
                      style={{
                        "--banner-top-height":
                          bannerTop && bannerTopMenuHeight ? `${bannerTopMenuHeight}px` : undefined,
                        "--header-height": headerHeight
                          ? `${Math.floor(headerHeight)}px`
                          : undefined,
                      }}
                    >
                      {item?.categoryMenuFieldSet
                        ?.filter((child) => !!child)
                        .map((child) => (
                          <div
                            className={classNames(
                              item.categoryMenuFieldSet?.length < 8 && "col-lg-3",
                              item.categoryMenuFieldSet?.length > 7 && "col"
                            )}
                            key={child.categoryMenu.id}
                          >
                            <div className="box-megamenu">
                              {child.categoryMenu.slug &&
                                ((child.categoryMenu.lineImageFieldSet.lineImage && (
                                  <GatsbyImage
                                    image={getImage(
                                      child.categoryMenu.lineImageFieldSet.lineImage.node
                                    )}
                                    className="box-megamenu__img d-none d-lg-block"
                                    alt={
                                      child.categoryMenu.lineImageFieldSet.lineImage.value
                                        ?.description ||
                                      child.categoryMenu.lineImageFieldSet?.altImage?.value ||
                                      ""
                                    }
                                  />
                                )) ||
                                  (child.categoryMenu.menuImage && (
                                    <GatsbyImage
                                      image={getImage(child.categoryMenu.menuImage.node)}
                                      className="box-megamenu__img d-none d-lg-block"
                                      alt={child.categoryMenu.menuImage?.value?.description || ""}
                                    />
                                  )))}
                              {child.categoryMenu.externalLink && child.categoryMenu.menuImage && (
                                <GatsbyImage
                                  image={getImage(child.categoryMenu.menuImage.node)}
                                  className="box-megamenu__img d-none d-lg-block"
                                  alt={child.categoryMenu.menuImage?.value?.description || ""}
                                />
                              )}
                              <div>
                                {child.categoryMenu.slug ? (
                                  <Link
                                    className="box-megamenu__link"
                                    to={linKFromSlug(child.categoryMenu.slug)}
                                  >
                                    {child.categoryMenu.pageNameMenu
                                      ? child.categoryMenu.pageNameMenu
                                      : child.categoryMenu.liferayFields.title}
                                  </Link>
                                ) : (
                                  child.categoryMenu.externalLink && (
                                    <a
                                      className="box-megamenu__link"
                                      href={child.categoryMenu.externalLink}
                                      target={
                                        child.categoryMenu.target
                                          ? child.categoryMenu.target?.[0]
                                          : ""
                                      }
                                      rel={
                                        child.categoryMenu.target &&
                                        child.categoryMenu.target?.[0] === "_blank"
                                          ? "noopener noreferrer"
                                          : ""
                                      }
                                    >
                                      {child.categoryMenu.liferayFields.title}
                                    </a>
                                  )
                                )}
                              </div>
                              {child.categoryMenuFiltered && (
                                <div>
                                  {child.categoryMenuFiltered.map((option) =>
                                    option.slug ? (
                                      <Link
                                        className="box-megamenu__filter"
                                        to={linKFromSlug(option.slug)}
                                        key={option.id}
                                      >
                                        {option.pageNameMenu
                                          ? option.pageNameMenu
                                          : option.liferayFields.title}
                                      </Link>
                                    ) : (
                                      option.externalLink && (
                                        <a
                                          className="box-megamenu__filter"
                                          href={option.externalLink}
                                          target={option.target ? option.target?.[0] : ""}
                                          rel={
                                            option.target && option.target?.[0] === "_blank"
                                              ? "noopener noreferrer"
                                              : ""
                                          }
                                          key={option.id}
                                        >
                                          {option.liferayFields.title}
                                        </a>
                                      )
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                    {item.mainMenu.slug && (
                      <div className="d-block d-lg-none">
                        <Link className="navigation__link" to={linKFromSlug(item.mainMenu.slug)}>
                          {labelManagerLabels.seeAllProducts}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <UtilityLink className="d-lg-none" />
    </nav>
  );
};

export default Navigation;
export const query = graphql`
  {
    labelManagerLabels: labelManagerLabels {
      seeAllProducts
    }
    mainMenuFiltered {
      mainMenuFieldSet {
        mainMenu {
          ... on LiferayPage {
            id
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageExternalLink {
            id
            liferayFields {
              title
            }
            externalLink
            target
          }
          ... on LiferayPageLine {
            id
            liferayFields {
              title
            }
            slug
          }
          ... on LiferayPageCrossLine {
            id
            liferayFields {
              title
            }
            slug
          }
        }
        categoryMenuFieldSet {
          categoryMenu {
            ... on LiferayPage {
              id
              liferayFields {
                title
              }
              slug
              menuImage {
                node {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
                value {
                  description
                }
              }
            }
            ... on LiferayPageCrossLine {
              id
              liferayFields {
                title
              }
              slug
              menuImage {
                node {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
                value {
                  description
                }
              }
            }
            ... on LiferayPageExternalLink {
              id
              liferayFields {
                title
              }
              externalLink
              target
              menuImage {
                node {
                  gatsbyImageData(width: 500, layout: CONSTRAINED)
                }
                value {
                  description
                }
              }
            }
            ... on LiferayPageLine {
              id
              liferayFields {
                title
              }
              pageNameMenu
              slug
              lineImageFieldSet {
                lineImage {
                  node {
                    gatsbyImageData(width: 500, layout: CONSTRAINED)
                  }
                }
                altImage
              }
              allBabelProdotto {
                id
              }
            }
          }
          categoryMenuFiltered {
            ... on LiferayPage {
              id
              liferayFields {
                title
              }
              slug
            }
            ... on LiferayPageCrossLine {
              id
              liferayFields {
                title
              }
              slug
            }
            ... on LiferayPageExternalLink {
              id
              liferayFields {
                title
              }
              externalLink
              target
            }
            ... on LiferayPageLine {
              id
              liferayFields {
                title
              }
              pageNameMenu
              slug
              allBabelProdotto {
                id
              }
            }
          }
        }
      }
    }
  }
`;
